import * as React from 'react';

import { Translate } from '@decorators';

import {
	BrandAppIconScreen,
	BrandBadgeScreen,
	BrandBlockScreen,
	BrandFontsScreen,
	BrandFooter,
	BrandGradientsScreen,
	BrandHeader,
	BrandIllustrationsScreen,
	BrandLogogramScreen,
	BrandLogotypeScreen,
	BrandMainScreen,
	BrandPaletteScreen,
	BrandPatternsScreen,
	BrandScreen,
} from '@common/containers/Brand/components';
import {
	EBrandMenuItem,
	EBrandScreen,
} from '@common/containers/Brand/enums';
import { IBrandPageProps } from '@common/containers/Brand/types';

import { IBrandPageState } from './types';

import * as s from './BrandPage.scss';

const GLOBAL_FIXED_CLASS_NAME = 'fixed';

@Translate('common.components.BrandPage')
export class BrandPage extends React.PureComponent<IBrandPageProps, IBrandPageState> {

	intersectionObserver: IntersectionObserver = null;

	lastMenuItemRef: React.RefObject<HTMLDivElement> = React.createRef();

	state: IBrandPageState = {
		isHeaderVisible: false,
	};

	componentDidMount() {
		if (window.IntersectionObserver) {
			this.intersectionObserver = new IntersectionObserver(this.onIntersect, {
				threshold: 0,
				rootMargin: '-10px',
			});
			this.intersectionObserver.observe(this.lastMenuItemRef.current);
		}
	}

	componentWillUnmount() {
		this.intersectionObserver?.disconnect();
	}

	getScreenTitle(screenId: EBrandScreen) {
		const {
			isMobile,
		} = this.props;

		if (isMobile && screenId === EBrandScreen.BADGE) {
			return this.t('badgeForMobile');
		}

		return this.t(screenId);
	}

	onIntersect = (entries: IntersectionObserverEntry[]) => {
		const {
			isIntersecting,
			boundingClientRect: {
				top,
			},
		} = entries[0];

		const isAboveViewport = top < 0;

		this.setState({
			isHeaderVisible: !isIntersecting && isAboveViewport,
		});
	};

	onMenuItemSelect = (menuItem: EBrandMenuItem) => {
		window.scrollTo({
			top: document.getElementById(menuItem).offsetTop,
			left: 0,
			behavior: 'smooth',
		});
	};

	onMenuShow = () => {
		document.body.classList.add(GLOBAL_FIXED_CLASS_NAME);
	};

	onMenuHide = () => {
		document.body.classList.remove(GLOBAL_FIXED_CLASS_NAME);
	};

	render() {
		const {
			classNames: {
				content: contentClassName,
			},
		} = this.props;
		const {
			isHeaderVisible,
		} = this.state;

		return (
			<div className={s.block}>
				<BrandHeader
					{...this.props}
					isHeaderVisible={isHeaderVisible}
					onMenuItemSelect={this.onMenuItemSelect}
					onMenuShow={this.onMenuShow}
					onMenuHide={this.onMenuHide}
				/>

				<div className={contentClassName}>
					<BrandMainScreen
						{...this.props}
						lastMenuItemRef={this.lastMenuItemRef}
						onMenuItemSelect={this.onMenuItemSelect}
					/>

					{Object.values(EBrandScreen).map((screenId: EBrandScreen) => (
						<BrandScreen
							key={screenId}
							id={screenId}
							title={this.getScreenTitle(screenId)}
						>
							{screenId === EBrandScreen.LOGOTYPE && <BrandLogotypeScreen {...this.props}/>}
							{screenId === EBrandScreen.LOGOGRAM && <BrandLogogramScreen {...this.props}/>}
							{screenId === EBrandScreen.PALETTE && <BrandPaletteScreen {...this.props}/>}
							{screenId === EBrandScreen.FONTS && <BrandFontsScreen {...this.props}/>}
							{screenId === EBrandScreen.BLOCK && <BrandBlockScreen {...this.props}/>}
							{screenId === EBrandScreen.BADGE && <BrandBadgeScreen {...this.props}/>}
							{screenId === EBrandScreen.APP_ICON && <BrandAppIconScreen {...this.props}/>}
							{screenId === EBrandScreen.ILLUSTRATIONS && <BrandIllustrationsScreen {...this.props}/>}
							{screenId === EBrandScreen.GRADIENTS && <BrandGradientsScreen {...this.props}/>}
							{screenId === EBrandScreen.PATTERNS && <BrandPatternsScreen {...this.props}/>}
						</BrandScreen>
					))}
				</div>

				<BrandFooter {...this.props}/>
			</div>
		);
	}

}
